/** Files attached to a QuantisAI message: uploaded first, then sent by id with the message. The server re-checks type and size from the bytes. */
import { API_BASE, getAuthHeaders } from '../../utils/supabase/client';
import { fetchSIJson } from './siRequest';

export type SIAttachment = { id: string; name: string; mimeType: string; size: number };

export const SI_ATTACHMENT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'] as const;
export const SI_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const SI_ATTACHMENTS_PER_MESSAGE = 3;
export const SI_ATTACHMENT_MESSAGE_BYTES = 15 * 1024 * 1024;
export const SI_ATTACHMENT_TOTAL_MESSAGE = 'Attachments on one message can total at most 15 MB. Remove one to send.';
/** The server refuses the whole message past this, so it is checked before sending. */
export function attachmentsTotalProblem(files: readonly { size: number }[]): string | null {
  return files.reduce((total, file) => total + file.size, 0) > SI_ATTACHMENT_MESSAGE_BYTES ? SI_ATTACHMENT_TOTAL_MESSAGE : null;
}

export function formatAttachmentSize(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A quick answer before uploading; null when the file may be sent. */
export function attachmentProblem(file: File): string | null {
  if (!(SI_ATTACHMENT_TYPES as readonly string[]).includes(file.type)) return 'Attach a PNG, JPEG or WebP image, or a PDF.';
  if (file.size > SI_ATTACHMENT_MAX_BYTES) return 'Attach files of up to 10 MB.';
  if (!file.size) return 'That file is empty.';
  return null;
}

export async function uploadSIAttachment(file: File, scope: { workspaceId: string; conversationId: string }, signal?: AbortSignal): Promise<SIAttachment> {
  const form = new FormData();
  form.append('workspaceId', scope.workspaceId);
  form.append('conversationId', scope.conversationId);
  form.append('file', file, file.name);
  let reply: { response: Response; value: { attachment?: SIAttachment; error?: string } };
  // A proxy can answer an oversized body with HTML, which is not worth showing.
  try { reply = await fetchSIJson('/si/attachments', { method: 'POST', body: form, signal }, 60000); }
  catch (cause) {
    if (signal?.aborted) throw cause;
    throw new Error('The file could not be attached. Please retry.', { cause });
  }
  if (!reply.response.ok || !reply.value.attachment) throw new Error(reply.value.error ?? 'The file could not be attached. Please retry.');
  return reply.value.attachment;
}

/** Best-effort: anything left behind is swept by the server once it is a day old. */
export async function deleteSIAttachment(id: string): Promise<void> {
  try { await fetchSIJson(`/si/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' }); } catch { /* Swept later. */ }
}

/** The bytes of a file sent with a message in this chat, for its sender. */
export async function fetchSIAttachment(attachmentId: string, scope: { workspaceId: string; conversationId: string }): Promise<Blob> {
  const path = `/si/history/${encodeURIComponent(scope.conversationId)}/attachments/${encodeURIComponent(attachmentId)}?workspaceId=${encodeURIComponent(scope.workspaceId)}`;
  const response = await fetch(`${API_BASE}${path}`, { headers: await getAuthHeaders(), signal: AbortSignal.timeout(60000) });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? 'The file could not be opened. Please retry.');
  }
  return response.blob();
}

/**
 * Opens a sent file in a new tab. The tab is opened during the click, before
 * the download, because browsers block a window opened after an await; when
 * one is blocked anyway, the file downloads instead.
 */
export async function openSIAttachment(attachment: SIAttachment, scope: { workspaceId: string; conversationId: string }): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const url = URL.createObjectURL(await fetchSIAttachment(attachment.id, scope));
    if (tab && !tab.closed) tab.location.href = url;
    else { const link = document.createElement('a'); link.href = url; link.download = attachment.name; link.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (cause) { tab?.close(); throw cause; }
}
